import api from './axiosConfig';

// ═══════════════════════════════════════════════════════════════════════════════
// AUTH
// ═══════════════════════════════════════════════════════════════════════════════

export const authApi = {
  /** POST /api/auth/login */
  login: (email, password) =>
    api.post('/api/auth/login', { email, password }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — DASHBOARD
// ═══════════════════════════════════════════════════════════════════════════════

export const dashboardApi = {
  /** GET /api/admin/stats */
  getStats: () => api.get('/api/admin/stats'),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — USER MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════════

export const usersApi = {
  /** GET /api/admin/users */
  getAll: (params = {}) => api.get('/api/admin/users', { params }),

  /** GET /api/admin/users/:id */
  getById: (id) => api.get(`/api/admin/users/${id}`),

  /** PUT /api/admin/users/:id/status */
  updateStatus: (id, status) =>
    api.put(`/api/admin/users/${id}/status`, { status }),

  /** DELETE /api/admin/users/:id */
  delete: (id) => api.delete(`/api/admin/users/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — ADMIN MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════════

export const adminMgmtApi = {
  /** GET /api/admin/admins */
  getAll: () => api.get('/api/admin/admins'),

  /** POST /api/admin/admins — promote user to admin */
  create: (data) => api.post('/api/admin/admins', data),

  /** PUT /api/admin/admins/:id/role */
  updateRole: (id, adminRole) =>
    api.put(`/api/admin/admins/${id}/role`, { adminRole }),

  /** DELETE /api/admin/admins/:id — revoke admin access */
  revoke: (id) => api.delete(`/api/admin/admins/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — BATTLES
// ═══════════════════════════════════════════════════════════════════════════════

export const battlesApi = {
  /** GET /api/admin/battles?page=0&size=20&status= */
  getAll: (params = {}) => api.get('/api/admin/battles', { params }),

  /** POST /api/admin/battles — create new battle */
  create: (data) => api.post('/api/admin/battles', data),

  /** PUT /api/admin/battles/:id — edit battle */
  update: (id, data) => api.put(`/api/admin/battles/${id}`, data),

  /** PUT /api/admin/battles/:id/status */
  updateStatus: (id, status) =>
    api.put(`/api/admin/battles/${id}/status`, { status }),

  /** DELETE /api/admin/battles/:id */
  delete: (id) => api.delete(`/api/admin/battles/${id}`),

  /** GET /api/admin/battles/:id/participants */
  getParticipants: (id) => api.get(`/api/admin/battles/${id}/participants`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — WITHDRAWALS
// ═══════════════════════════════════════════════════════════════════════════════

export const withdrawalsApi = {
  /** GET /api/admin/withdrawals */
  getAll: (params = {}) => api.get('/api/admin/withdrawals', { params }),

  /** PUT /api/admin/withdrawals/:id/status */
  updateStatus: (id, status) =>
    api.put(`/api/admin/withdrawals/${id}/status`, { status }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — REPORTS
// ═══════════════════════════════════════════════════════════════════════════════

export const reportsApi = {
  /** GET /api/admin/reports/users */
  getUsers: (params = {}) => api.get('/api/admin/reports/users', { params }),

  /** GET /api/admin/reports/battles */
  getBattles: () => api.get('/api/admin/reports/battles'),

  /** GET /api/admin/reports/withdrawals */
  getWithdrawals: () => api.get('/api/admin/reports/withdrawals'),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — EMAIL
// ═══════════════════════════════════════════════════════════════════════════════

export const emailApi = {
  /** GET /api/admin/email/stats */
  getStats: (adminSecret) =>
    api.get('/api/admin/email/stats', {
      headers: { 'X-Admin-Secret': adminSecret },
    }),

  /** GET /api/admin/email/logs */
  getLogs: (adminSecret, params = {}) =>
    api.get('/api/admin/email/logs', {
      headers: { 'X-Admin-Secret': adminSecret },
      params,
    }),

  /** POST /api/admin/email/send-update */
  sendUpdate: (adminSecret, data) =>
    api.post('/api/admin/email/send-update', data, {
      headers: { 'X-Admin-Secret': adminSecret },
    }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — PROFILE
// ═══════════════════════════════════════════════════════════════════════════════

export const profileApi = {
  /** PUT /api/admin/profile */
  update: (data) => api.put('/api/admin/profile', data),

  /** POST /api/users/upload/avatar */
  uploadAvatar: (formData) =>
    api.post('/api/users/upload/avatar', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// LEADERBOARD
// ═══════════════════════════════════════════════════════════════════════════════

export const leaderboardApi = {
  getCity: (city = 'Delhi') =>
    api.get('/api/leaderboard/city', { params: { city } }),

  getStreak: () => api.get('/api/leaderboard/streak'),

  getTerritory: () => api.get('/api/leaderboard/territory'),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — WALLET MANAGEMENT
// ═══════════════════════════════════════════════════════════════════════════════

export const walletAdminApi = {
  /** GET /api/admin/wallet/stats */
  getStats: () => api.get('/api/admin/wallet/stats'),

  /** GET /api/admin/wallet/all-transactions */
  getAllTransactions: (params = {}) =>
    api.get('/api/admin/wallet/all-transactions', { params }),

  /** GET /api/admin/wallet/user/:userId */
  getUserWallet: (userId) => api.get(`/api/admin/wallet/user/${userId}`),

  /** POST /api/admin/wallet/add-coins */
  addCoins: (data) => api.post('/api/admin/wallet/add-coins', data),

  /** POST /api/admin/wallet/deduct-coins */
  deductCoins: (data) => api.post('/api/admin/wallet/deduct-coins', data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — NOTIFICATIONS (broadcast + individual)
// ═══════════════════════════════════════════════════════════════════════════════

export const notifAdminApi = {
  /** POST /api/admin/notifications/broadcast */
  broadcast: (data) => api.post('/api/admin/notifications/broadcast', data),

  /** POST /api/admin/notifications/send */
  sendToUser: (data) => api.post('/api/admin/notifications/send', data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — PAYMENT GATEWAYS
// ═══════════════════════════════════════════════════════════════════════════════

export const gatewayApi = {
  /** GET /api/admin/payment/gateways */
  list: () => api.get('/api/admin/payment/gateways'),

  /** PUT /api/admin/payment/gateways/:code/toggle */
  toggle: (code) => api.put(`/api/admin/payment/gateways/${code}/toggle`),

  /** PUT /api/admin/payment/gateways/:code/config */
  configure: (code, data) => api.put(`/api/admin/payment/gateways/${code}/config`, data),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — CRON JOB SCHEDULER
// ═══════════════════════════════════════════════════════════════════════════════

export const cronApi = {
  /** GET /api/admin/cron */
  list: () => api.get('/api/admin/cron'),

  /** POST /api/admin/cron */
  create: (data) => api.post('/api/admin/cron', data),

  /** PUT /api/admin/cron/:id */
  update: (id, data) => api.put(`/api/admin/cron/${id}`, data),

  /** PUT /api/admin/cron/:id/toggle */
  toggle: (id) => api.put(`/api/admin/cron/${id}/toggle`),

  /** POST /api/admin/cron/:id/run-now */
  runNow: (id) => api.post(`/api/admin/cron/${id}/run-now`),

  /** DELETE /api/admin/cron/:id */
  delete: (id) => api.delete(`/api/admin/cron/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — WALK SESSIONS
// ═══════════════════════════════════════════════════════════════════════════════

export const walkSessionsApi = {
  /** GET /api/admin/walk-sessions?page=0&size=20&status=&userId= */
  getAll: (params = {}) => api.get('/api/admin/walk-sessions', { params }),

  /** GET /api/admin/walk-sessions/stats */
  getStats: () => api.get('/api/admin/walk-sessions/stats'),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — CHALLENGES
// ═══════════════════════════════════════════════════════════════════════════════

export const challengesApi = {
  /** GET /api/admin/challenges?type= */
  getAll: (params = {}) => api.get('/api/admin/challenges', { params }),

  /** POST /api/admin/challenges */
  create: (data) => api.post('/api/admin/challenges', data),

  /** PUT /api/admin/challenges/:id */
  update: (id, data) => api.put(`/api/admin/challenges/${id}`, data),

  /** DELETE /api/admin/challenges/:id */
  delete: (id) => api.delete(`/api/admin/challenges/${id}`),

  /** PUT /api/admin/challenges/:id/toggle */
  toggle: (id) => api.put(`/api/admin/challenges/${id}/toggle`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — REFERRALS
// ═══════════════════════════════════════════════════════════════════════════════

export const referralsApi = {
  /** GET /api/admin/referrals?page=0&size=20 */
  getAll: (params = {}) => api.get('/api/admin/referrals', { params }),

  /** GET /api/admin/referrals/stats */
  getStats: () => api.get('/api/admin/referrals/stats'),

  /** GET /api/admin/referrals/:code/uses */
  getUses: (code) => api.get(`/api/admin/referrals/${code}/uses`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — REWARDS
// ═══════════════════════════════════════════════════════════════════════════════

export const rewardsApi = {
  /** GET /api/admin/rewards */
  getAll: () => api.get('/api/admin/rewards'),

  /** POST /api/admin/rewards */
  create: (data) => api.post('/api/admin/rewards', data),

  /** PUT /api/admin/rewards/:id */
  update: (id, data) => api.put(`/api/admin/rewards/${id}`, data),

  /** DELETE /api/admin/rewards/:id */
  delete: (id) => api.delete(`/api/admin/rewards/${id}`),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — APP CONFIG
// ═══════════════════════════════════════════════════════════════════════════════

export const appConfigApi = {
  /** GET /api/admin/config */
  getAll: () => api.get('/api/admin/config'),

  /** PUT /api/admin/config */
  update: (data) => api.put('/api/admin/config', data),

  /** PUT /api/admin/config/:key */
  updateKey: (key, value) => api.put(`/api/admin/config/${key}`, { value }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — LEADERBOARD (admin view)
// ═══════════════════════════════════════════════════════════════════════════════

export const adminLeaderboardApi = {
  /** GET /api/admin/leaderboard?type=daily|weekly&city= */
  get: (params = {}) => api.get('/api/admin/leaderboard', { params }),
};

// ═══════════════════════════════════════════════════════════════════════════════
// ADMIN — CHART DATA (dashboard)
// ═══════════════════════════════════════════════════════════════════════════════

export const chartApi = {
  /** GET /api/admin/stats/chart — last 7 days daily breakdown */
  getChart: () => api.get('/api/admin/stats/chart'),
};
